import {
  isFemaEnvironmentClass,
  isFemaMetaEnvironment,
  type FemaAlertScope,
  type FemaAlertStatus,
  type FemaEnvironment,
  type FemaEnvironmentClass,
  type FemaJurisdictions,
  type FemaMeta,
  type FemaMetaEnvironment,
  type FemaMetaTenant,
} from 'types/fema.types';
import {
  FEMA_ENVIRONMENT_NOT_AVAILABLE,
  FEMA_ENVIRONMENT_NOT_HELD,
  FEMA_ENVIRONMENT_OPTIONS,
  type FemaEnvironmentChoice,
  type FemaEnvironmentOption,
} from '../components/IpawsAlertFields/config/femaEnvironment.config';

/**
 * The part of `GET /fusion/v2/fema/meta` that the environment helpers read. `environments` and
 * `defaultEnvironment` are absent from an older backend (multi-COG contract §2).
 */
export type FemaEnvironmentMetaInput =
  | Partial<Pick<FemaMeta, 'environment' | 'tenants' | 'environments' | 'defaultEnvironment'>>
  | null
  | undefined;

/** Type guard for a stored or typed value. */
export const isFemaEnvironmentChoice = (value: unknown): value is FemaEnvironmentChoice =>
  isFemaEnvironmentClass(value);

/**
 * Reads a class token from a stored record or an answer. The comparison ignores case and space.
 * @returns The lower-case class token, or `null` for any other value.
 */
export const toFemaEnvironmentClass = (value: unknown): FemaEnvironmentClass | null => {
  if (typeof value !== 'string') return null;
  const token = value.trim().toLowerCase();
  return isFemaEnvironmentClass(token) ? token : null;
};

/**
 * Reads the per-class list of the multi-COG `/meta` (contract §2).
 * @returns The valid entries, or `null` when the meta has no `environments` array (an older
 *   backend). An entry that fails the guard is left out.
 */
export const readFemaMetaEnvironments = (
  meta: FemaEnvironmentMetaInput,
): FemaMetaEnvironment[] | null =>
  Array.isArray(meta?.environments) ? meta.environments.filter(isFemaMetaEnvironment) : null;

/** @returns The `/meta` entry for the class, or `null` when the workplace does not hold it. */
export const getFemaMetaEnvironmentEntry = (
  meta: FemaEnvironmentMetaInput,
  choice: unknown,
): FemaMetaEnvironment | null =>
  readFemaMetaEnvironments(meta)?.find((entry) => entry.environment === choice) ?? null;

/**
 * Finds the option for the class that the service serves. The comparison ignores case.
 * @returns The option's token, or `null` when the meta names no class or an unknown class.
 */
export const getServedFemaEnvironmentChoice = (
  meta: FemaEnvironmentMetaInput,
): FemaEnvironmentChoice | null => {
  const served = String(meta?.environment ?? '')
    .trim()
    .toUpperCase();
  if (!served) return null;
  return FEMA_ENVIRONMENT_OPTIONS.find((option) => option.environment === served)?.value ?? null;
};

/**
 * The class that the select stores when the draft holds none.
 * Multi-COG meta: `defaultEnvironment`, never `production` (contract §0 and §2).
 * Older meta: the class that the service serves.
 */
export const getDefaultFemaEnvironmentChoice = (
  meta: FemaEnvironmentMetaInput,
): FemaEnvironmentChoice | null => {
  if (readFemaMetaEnvironments(meta) === null) return getServedFemaEnvironmentChoice(meta);
  const choice = toFemaEnvironmentClass(meta?.defaultEnvironment);
  return choice === 'production' ? null : choice;
};

const describeCog = (cogId: string, name: string | null | undefined): string =>
  name ? `COG ${cogId} (${name})` : `COG ${cogId}`;

const describeTenant = (tenant: FemaMetaTenant): string =>
  describeCog(tenant.cogId, tenant.cogName ?? tenant.organizationName);

/** The COG list for the enabled option, for example `COG 120188 (RescAlert Inc.)`. */
export const describeFemaEnvironmentCogs = (meta: FemaEnvironmentMetaInput): string =>
  Array.isArray(meta?.tenants) ? meta.tenants.map(describeTenant).join(', ') : '';

/** One select option, as the select renders it. */
export type FemaEnvironmentOptionState = {
  option: FemaEnvironmentOption;
  /** True when the operator can select the option. */
  enabled: boolean;
  /** The option text. */
  text: string;
};

/**
 * The three select options. The option text is the class label only: the COG is masked
 * everywhere it is shown (final polish `085af8d2e`), so the option names no COG.
 * Multi-COG meta: an option is enabled when its class is held and `canSend` is true. A held class
 * that cannot send is disabled. A class that the workplace does not hold is disabled and marked
 * "not held by this workplace".
 * Older meta: only the class that the service serves is enabled.
 */
export const getFemaEnvironmentOptionStates = (
  meta: FemaEnvironmentMetaInput,
): FemaEnvironmentOptionState[] => {
  const entries = readFemaMetaEnvironments(meta);
  if (entries === null) {
    const servedChoice = getServedFemaEnvironmentChoice(meta);
    return FEMA_ENVIRONMENT_OPTIONS.map((option) => ({
      option,
      enabled: option.value === servedChoice,
      text: option.label,
    }));
  }
  return FEMA_ENVIRONMENT_OPTIONS.map((option) => {
    const entry = entries.find((candidate) => candidate.environment === option.value);
    if (!entry) {
      return { option, enabled: false, text: `${option.label} — ${FEMA_ENVIRONMENT_NOT_HELD}` };
    }
    return { option, enabled: entry.canSend, text: option.label };
  });
};

const joinLabels = (labels: string[]): string =>
  labels.length <= 1
    ? (labels[0] ?? '')
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;

const endSentence = (text: string): string => (/[.!?]$/.test(text) ? text : `${text}.`);

/**
 * The sentence under the select.
 * Multi-COG meta: the `reason` of each held class that cannot send, then the classes that the
 * workplace does not hold.
 * Older meta: each class that is not available, and the class that the service serves.
 * @returns An empty string when every class is available.
 */
export const describeUnavailableFemaEnvironments = (meta: FemaEnvironmentMetaInput): string => {
  const entries = readFemaMetaEnvironments(meta);
  if (entries !== null) {
    const sentences: string[] = [];
    FEMA_ENVIRONMENT_OPTIONS.forEach((option) => {
      const entry = entries.find((candidate) => candidate.environment === option.value);
      if (entry && !entry.canSend) {
        sentences.push(
          `${option.label}: ${endSentence(entry.reason ?? FEMA_ENVIRONMENT_NOT_AVAILABLE)}`,
        );
      }
    });
    const notHeld = FEMA_ENVIRONMENT_OPTIONS.filter(
      (option) => !entries.some((entry) => entry.environment === option.value),
    );
    if (notHeld.length) {
      const verb = notHeld.length === 1 ? 'is' : 'are';
      sentences.push(
        `${joinLabels(notHeld.map((option) => option.label))} ${verb} ${FEMA_ENVIRONMENT_NOT_HELD}.`,
      );
    }
    return sentences.join(' ');
  }

  const servedChoice = getServedFemaEnvironmentChoice(meta);
  const unavailable = FEMA_ENVIRONMENT_OPTIONS.filter((option) => option.value !== servedChoice);
  if (unavailable.length === 0) return '';

  const servedOption = FEMA_ENVIRONMENT_OPTIONS.find((option) => option.value === servedChoice);
  const servedName = servedOption?.shortLabel ?? String(meta?.environment ?? '').toUpperCase();
  const verb = unavailable.length === 1 ? 'is' : 'are';
  const labels = joinLabels(unavailable.map((option) => option.label));
  const served = servedName ? ` The backend serves ${servedName} only.` : '';
  return `${labels} ${verb} not available yet.${served}`;
};

/**
 * The draft finding for a class that cannot be used.
 * Multi-COG meta: the stored class must be held and able to send.
 * Older meta: the stored class must be the class that the service serves.
 * @returns The finding, or `null` when nothing is stored, the meta is absent, or the class is usable.
 */
export const getFemaEnvironmentFinding = (
  stored: unknown,
  meta: FemaEnvironmentMetaInput,
): { fieldPath: 'femaEnvironment'; message: string } | null => {
  if (stored === null || stored === undefined || stored === '' || !meta) return null;
  const entries = readFemaMetaEnvironments(meta);
  const isUsable =
    entries === null
      ? stored === getServedFemaEnvironmentChoice(meta)
      : entries.some((entry) => entry.environment === stored && entry.canSend);
  return isUsable
    ? null
    : { fieldPath: 'femaEnvironment', message: FEMA_ENVIRONMENT_NOT_AVAILABLE };
};

/**
 * The class that a NEW send carries as the multipart field `environment` (contract §1).
 * @returns The stored class when the meta serves `environments[]`; otherwise `undefined`, so that
 *   an older backend receives no class.
 */
export const getWireFemaEnvironment = (
  meta: FemaEnvironmentMetaInput,
  stored: unknown,
): FemaEnvironmentClass | undefined =>
  readFemaMetaEnvironments(meta) !== null && isFemaEnvironmentClass(stored) ? stored : undefined;

/**
 * THE ONE READER of the FEMA class in play. Every caller that must know the class (the Alert
 * Creator status and scope, the preview, the WEA Handling filter, the dev tools gate, the Global
 * Action editor) reads it here.
 * - A chosen class token wins (the draft's select, or the Global Action's own choice).
 * - Nothing chosen, older meta (no `environments[]`): the class that the service serves.
 * - Nothing chosen, multi-COG meta: `null`. No class is assumed, and production never is
 *   (contract §0); the select default comes from `defaultEnvironment`.
 * @returns The lower-case class token, or `null` while the class is not known.
 */
export const resolveFemaEnvironmentClass = (
  chosen: unknown,
  meta: FemaEnvironmentMetaInput,
): FemaEnvironmentChoice | null => {
  if (isFemaEnvironmentChoice(chosen)) return chosen;
  return readFemaMetaEnvironments(meta) === null ? getServedFemaEnvironmentChoice(meta) : null;
};

/**
 * The upper-case class that the preview, the WEA Handling filter and the dev tools gate read.
 * It is `resolveFemaEnvironmentClass` in upper case.
 */
export const getChosenFemaEnvironment = (
  meta: FemaEnvironmentMetaInput,
  stored: unknown,
): FemaEnvironment | undefined =>
  FEMA_ENVIRONMENT_OPTIONS.find(
    (option) => option.value === resolveFemaEnvironmentClass(stored, meta),
  )?.environment;

/**
 * Checks the class that `/jurisdictions` echoes against the class that the caller asked for
 * (contract §1). The comparison ignores case: an older backend echoes `TDL`.
 * @returns The answer when it matches, when nothing was asked, or when the answer names no class.
 *   On a mismatch: no data, and `mismatch: true`.
 */
export const acceptFemaJurisdictionsForClass = (
  data: FemaJurisdictions | undefined,
  requested: unknown,
): { data: FemaJurisdictions | undefined; mismatch: boolean } => {
  if (!data || !isFemaEnvironmentClass(requested)) return { data, mismatch: false };
  const echoed = toFemaEnvironmentClass(data.environment);
  if (echoed === null || echoed === requested) return { data, mismatch: false };
  return { data: undefined, mismatch: true };
};

/** The label for the details block, for example `TDL / Staging`. */
export const getFemaEnvironmentLabel = (choice: unknown): string | null =>
  FEMA_ENVIRONMENT_OPTIONS.find((option) => option.value === choice)?.label ?? null;

/** The option for a class token, or `null`. */
export const getFemaEnvironmentOption = (choice: unknown): FemaEnvironmentOption | null =>
  FEMA_ENVIRONMENT_OPTIONS.find((option) => option.value === choice) ?? null;

/**
 * Status and Scope by FEMA class (Mike, 2026-09-26), for the Alert Creator and Global Actions
 * alike: TDL (RescAlert's own testing) and DEMO (clients testing) offer every status and scope;
 * PRODUCTION sends Actual and Public only, with every other status and scope greyed out.
 */

/** The status a PRODUCTION message always carries. */
export const PRODUCTION_STATUS: FemaAlertStatus = 'Actual';
/** The scope a PRODUCTION message always carries. */
export const PRODUCTION_SCOPE: FemaAlertScope = 'Public';

/** Tells if a class offers every status (Test and Draft included). An unknown class does not. */
const offersEveryStatus = (femaClass: FemaEnvironmentChoice | null): boolean =>
  femaClass === 'tdl' || femaClass === 'demo';

/** Tells if a status can be sent in a class: any status in TDL and DEMO, else Actual only. */
export const isIpawsStatusAllowedForClass = (
  status: unknown,
  femaClass: FemaEnvironmentChoice | null,
): boolean => offersEveryStatus(femaClass) || status === PRODUCTION_STATUS;

/** Tells if a scope can be sent in a class. Only PRODUCTION restricts the scope. */
export const isIpawsScopeAllowedForClass = (
  scope: unknown,
  femaClass: FemaEnvironmentChoice | null,
): boolean => femaClass !== 'production' || scope === PRODUCTION_SCOPE;

/** An option as the select receives it: `isDisabled` is set on an option a class does not allow. */
export type IpawsGreyableOption<Option extends { value: string }> = Option & {
  isDisabled?: boolean;
};

/** Greys out (`isDisabled`) the options a class does not allow; the select still lists them. */
export const greyOutIpawsOptions = <Option extends { value: string }>(
  options: readonly Option[],
  isAllowed: (value: string) => boolean,
): IpawsGreyableOption<Option>[] =>
  options.map((option) => (isAllowed(option.value) ? option : { ...option, isDisabled: true }));
